import { useState } from 'react';

import { useGraphQLMutation } from '@betterment/graphql';
import { useAutoNavigate } from '@betterment/js-runtime';
import { Banner, Box, Button, Dialog, Heading, Text } from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';
import * as Sentry from '@sentry/browser';
import { useLocation } from 'react-router-dom';

import { IdempotencyProviderWithFeatureRegistry } from '../../../../RetailApp/graphql/idempotency/IdempotencyProviderWithFeatureRegistry';
import { urlBuilder } from '../../../../Shared/urlBuilder';
import { DeletePurposeDocument } from '../graphql/mutation.deletePurpose.generated';

type DeletePurposeConfirmationVariant = 'PurposeSettings' | 'AccountSettings';

type DeletePurposeConfirmationProps = {
  purposeId: string;
  accountId: string;
  purposeName: string;
  accountName: string;
  variant: DeletePurposeConfirmationVariant;
};

export const DeletePurposeConfirmation: React.FC<DeletePurposeConfirmationProps> = ({
  purposeId,
  accountId,
  purposeName,
  accountName,
  variant,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [mutationErrorMessage, setMutationErrorMessage] = useState<string | undefined>();
  const [deletePurpose] = useGraphQLMutation(DeletePurposeDocument);
  const navigate = useAutoNavigate();
  const location = useLocation();

  const handleDeletePurpose = async () => {
    setIsLoading(true);

    try {
      const { data: mutationResult } = await deletePurpose({
        variables: { input: { purposeId }, accountId },
      });

      if (mutationResult) {
        const { displayableErrors } = mutationResult.deletePurpose;

        if (displayableErrors[0]) {
          setMutationErrorMessage(displayableErrors[0].message);
          return;
        }
      }

      setMutationErrorMessage(undefined);
      setIsLoading(false);

      const relativeTargetPath = urlBuilder.appAccount({ accountId });

      if (location.pathname === relativeTargetPath) {
        window.location.reload();
      } else {
        void navigate(urlBuilder.appAccount.absolute({ accountId }), { replace: true });
      }
    } catch (error) {
      Sentry.captureException(error);
      setMutationErrorMessage('An error occurred while removing the account. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <IdempotencyProviderWithFeatureRegistry featureName="deletePurpose">
      <Dialog>
        <Dialog.Trigger asChild={true}>
          {variant === 'PurposeSettings' ? (
            <Button variant="textCritical" css={{ margin: '0 auto' }}>
              Delete goal
            </Button>
          ) : (
            <Button variant="textPrimary">Remove</Button>
          )}
        </Dialog.Trigger>
        <Dialog.Content
          css={{
            display: 'grid',
            gap: 'space.150',
            p: 'space.400',
            overflowY: 'auto',
          }}
          size="large"
        >
          <Dialog.Title asChild={true}>
            <Heading as="h2" variant="title300">
              {variant === 'PurposeSettings'
                ? 'Are you sure you want to delete this goal?'
                : 'Remove this account from your goal?'}
            </Heading>
          </Dialog.Title>
          <Dialog.Description asChild={true}>
            <>
              {!!mutationErrorMessage && (
                <Banner variant="critical">
                  <Text>{mutationErrorMessage}</Text>
                </Banner>
              )}
              <Box>
                <Text>
                  This will delete your {purposeName} goal and keep only the {accountName} account.
                </Text>
                <ul className={css({ listStyleType: 'disc', pl: 'space.300' })}>
                  <li>Auto-adjust will be turned off for this account (if enabled).</li>
                  <li>
                    Any external accounts linked to the goal will be unassigned—you’ll find them on your home screen.
                  </li>
                  <li>Custom names or icons on the goal will disappear.</li>
                  <li>Your investment allocation won’t change, but you can update it at any time.</li>
                </ul>
              </Box>
              <Box css={{ display: 'flex', gap: 'space.200', alignItems: 'center' }}>
                <Button variant="solidPrimary" onClick={handleDeletePurpose} loading={isLoading}>
                  {variant === 'PurposeSettings' ? 'Delete goal' : 'Remove from goal'}
                </Button>
                <Dialog.Close asChild={true}>
                  <Button variant="ghostPrimary" disabled={isLoading}>
                    Cancel
                  </Button>
                </Dialog.Close>
              </Box>
            </>
          </Dialog.Description>
        </Dialog.Content>
      </Dialog>
    </IdempotencyProviderWithFeatureRegistry>
  );
};
